import type {
  CustomerContactFormInitialValues,
  CustomerContactFormSubmitPayload,
  useNotificationSnackbar,
} from "design-components";
import { getThunkErrorMessage, IconReactivateOutlined } from "design-components";
import type { ApiCustomerDetail } from "api-client";
import { ApiErrorHandler } from "api-client";
import { useCallback, useMemo, useRef, useState } from "react";
import type { AppDispatch } from "../../../shared/store/store";
import {
  clearContactEditDetail,
  createCustomerContact,
  deleteCustomerContact,
  fetchCustomerContact,
  fetchCustomerContacts,
  restoreCustomerContact,
  updateCustomerContact,
} from "../../../shared/store/customerDetail";
import {
  formatBulkContactDeleteDescription,
  formatBulkContactRestoreDescription,
  formatContactSuccessMessage,
  resolveCustomerOperatorId,
} from "../utils/customer-detail.page.utils";

type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];

const CONTACT_FORM_ERROR_FALLBACK = {
  create: "Failed to add contact.",
  edit: "Failed to update contact.",
} as const;

interface ContactListRow {
  contactId: number;
  fullName: string;
}

interface UseCustomerDetailContactsActionsParams {
  customerId: string | undefined;
  customer: ApiCustomerDetail | null;
  contactsRows: ContactListRow[];
  contactEditInitialValues: CustomerContactFormInitialValues | null;
  dispatch: AppDispatch;
  showNotification: ShowNotification;
}

export function useCustomerDetailContactsActions({
  customerId,
  customer,
  contactsRows,
  contactEditInitialValues,
  dispatch,
  showNotification,
}: UseCustomerDetailContactsActionsParams) {
  const [deleteContactsOpen, setDeleteContactsOpen] = useState(false);
  const [deleteContactsTargetIds, setDeleteContactsTargetIds] = useState<number[]>([]);
  const [contactDrawerMode, setContactDrawerMode] = useState<"create" | "edit">("create");
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [editContactId, setEditContactId] = useState<number | null>(null);
  const activeEditFetchContactIdRef = useRef<number | null>(null);

  const handleContactsAdd = useCallback(() => {
    activeEditFetchContactIdRef.current = null;
    dispatch(clearContactEditDetail());
    setContactDrawerMode("create");
    setEditContactId(null);
    setContactDrawerOpen(true);
  }, [dispatch]);

  const handleContactsEdit = useCallback(
    (contactId: number) => {
      if (!customerId || !customer) return;
      const operatorId = resolveCustomerOperatorId(customer);
      activeEditFetchContactIdRef.current = contactId;
      dispatch(clearContactEditDetail());
      setContactDrawerMode("edit");
      setEditContactId(contactId);
      setContactDrawerOpen(true);
      void dispatch(fetchCustomerContact({ customerId, contactId, operatorId }))
        .unwrap()
        .catch((e: unknown) => {
          if (activeEditFetchContactIdRef.current !== contactId) return;
          activeEditFetchContactIdRef.current = null;
          setContactDrawerOpen(false);
          setEditContactId(null);
          dispatch(clearContactEditDetail());
          showNotification({
            variant: "error",
            title: "Could not load contact",
            description: getThunkErrorMessage(e, "Failed to load contact."),
          });
        });
    },
    [customer, customerId, dispatch, showNotification],
  );

  const handleContactDrawerClose = useCallback(() => {
    activeEditFetchContactIdRef.current = null;
    setContactDrawerOpen(false);
    setEditContactId(null);
    dispatch(clearContactEditDetail());
  }, [dispatch]);

  const contactFormInitialValues = contactDrawerMode === "edit" ? contactEditInitialValues : null;

  const handleContactFormSubmit = useCallback(
    async (payload: CustomerContactFormSubmitPayload) => {
      if (!customerId || !customer) return;
      const operatorId = resolveCustomerOperatorId(customer);
      const body = {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        isPrimary: payload.isPrimary,
        roles: payload.roles,
      };
      const isCreate = contactDrawerMode === "create";
      const errorFallback = isCreate
        ? CONTACT_FORM_ERROR_FALLBACK.create
        : CONTACT_FORM_ERROR_FALLBACK.edit;

      try {
        if (isCreate) {
          const response = await dispatch(
            createCustomerContact({ customerId, operatorId, body }),
          ).unwrap();
          handleContactDrawerClose();
          const contactName = response.data.name.trim() || payload.name.trim() || "Contact";
          showNotification({
            variant: "success",
            title: "Contact added",
            description: formatContactSuccessMessage(contactName, "added"),
          });
          return;
        }

        const contactId = payload.contactId ?? editContactId;
        if (contactId == null) return;

        const response = await dispatch(
          updateCustomerContact({ customerId, contactId, operatorId, body }),
        ).unwrap();
        handleContactDrawerClose();
        const contactName = response.data.name.trim() || payload.name.trim() || "Contact";
        showNotification({
          variant: "success",
          title: "Contact updated",
          description: formatContactSuccessMessage(contactName, "updated"),
        });
      } catch (e: unknown) {
        showNotification({
          variant: "error",
          title: isCreate ? "Could not add contact" : "Could not update contact",
          description: getThunkErrorMessage(e, errorFallback),
        });
      }
    },
    [
      contactDrawerMode,
      customer,
      customerId,
      dispatch,
      editContactId,
      handleContactDrawerClose,
      showNotification,
    ],
  );

  const handleContactsDeleteRequest = useCallback((contactIds: number[]) => {
    if (contactIds.length === 0) return;
    setDeleteContactsTargetIds(contactIds);
    setDeleteContactsOpen(true);
  }, []);

  const handleDeleteContactsClose = useCallback(() => {
    setDeleteContactsOpen(false);
    setDeleteContactsTargetIds([]);
  }, []);

  const handleDeleteContactsConfirm = useCallback(async () => {
    if (!customerId || !customer || deleteContactsTargetIds.length === 0) return;

    const operatorId = resolveCustomerOperatorId(customer);
    const uniqueContactIds = [...new Set(deleteContactsTargetIds)];
    const contactNameById = new Map(
      uniqueContactIds.map((contactId) => [
        contactId,
        contactsRows.find((row) => row.contactId === contactId)?.fullName.trim() ?? "",
      ]),
    );
    handleDeleteContactsClose();

    const deleteResults = await Promise.allSettled(
      uniqueContactIds.map((contactId) =>
        dispatch(deleteCustomerContact({ customerId, contactId, operatorId })).unwrap(),
      ),
    );

    const succeededIds = uniqueContactIds.filter((_, index) => deleteResults[index].status === "fulfilled");
    const failedCount = uniqueContactIds.length - succeededIds.length;

    if (succeededIds.length > 0) {
      await dispatch(fetchCustomerContacts()).unwrap();
    }

    if (failedCount === uniqueContactIds.length) {
      const firstRejection = deleteResults.find((result) => result.status === "rejected");
      const message =
        firstRejection?.status === "rejected"
          ? getThunkErrorMessage(firstRejection.reason, "Failed to delete contact.")
          : "Failed to delete contact.";
      showNotification({
        variant: "error",
        title: "Delete failed",
        description: message,
      });
      return;
    }

    const count = succeededIds.length;
    const succeededNames = succeededIds
      .map((contactId) => contactNameById.get(contactId))
      .filter((name): name is string => Boolean(name));

    if (failedCount > 0) {
      showNotification({
        variant: "warning",
        title: "Partial delete",
        description: `${count} contact(s) removed. ${failedCount} could not be deleted.`,
      });
      return;
    }

    showNotification({
      variant: "success",
      title: "Contact deleted",
      description: formatBulkContactDeleteDescription(succeededNames, count),
      actions: [
        {
          label: "Undo",
          icon: <IconReactivateOutlined aria-hidden size={20} />,
          onClick: () => {
            void (async () => {
              try {
                const restoreResults = await Promise.allSettled(
                  succeededIds.map((contactId) =>
                    dispatch(restoreCustomerContact({ customerId, contactId, operatorId })).unwrap(),
                  ),
                );
                const restoreFailed = restoreResults.some((result) => result.status === "rejected");
                await dispatch(fetchCustomerContacts()).unwrap();
                if (restoreFailed) {
                  showNotification({
                    variant: "warning",
                    title: "Partial restore",
                    description: "Some contacts could not be restored.",
                  });
                  return;
                }
                showNotification({
                  variant: "success",
                  title: "Contact restored",
                  description: formatBulkContactRestoreDescription(succeededNames, count),
                });
              } catch (e: unknown) {
                showNotification({
                  variant: "error",
                  title: "Restore failed",
                  description: ApiErrorHandler.extractAxiosError(e, "Failed to restore contact."),
                });
              }
            })();
          },
        },
      ],
    });
  }, [
    contactsRows,
    customer,
    customerId,
    deleteContactsTargetIds,
    dispatch,
    handleDeleteContactsClose,
    showNotification,
  ]);

  const deleteContactsDescription = useMemo(() => {
    const count = deleteContactsTargetIds.length;
    if (count === 1) {
      const row = contactsRows.find((c) => c.contactId === deleteContactsTargetIds[0]);
      const name = row?.fullName.trim() || "this contact";
      return <>Are you sure you want to delete {name}?</>;
    }
    return <>Are you sure you want to delete {count} contacts?</>;
  }, [contactsRows, deleteContactsTargetIds]);

  return {
    deleteContactsOpen,
    deleteContactsTargetIds,
    contactDrawerMode,
    contactDrawerOpen,
    contactFormInitialValues,
    deleteContactsDescription,
    handleContactsAdd,
    handleContactsEdit,
    handleContactDrawerClose,
    handleContactFormSubmit,
    handleContactsDeleteRequest,
    handleDeleteContactsClose,
    handleDeleteContactsConfirm,
  };
}
